/**
 * Email CTA capture
 *
 * Records an `Email CTA Clicked` analytics event when a user lands on the app
 * via a marketing-email link (utm_source=email).
 *
 * Why this exists:
 * - PostHog's autocapture pageview is disabled (`capture_pageview: false` in
 *   analytics.ts). The manual `posthog.capture('$pageview')` in
 *   `usePageTimeTracking` only mounts on protected pages, by which time the
 *   auth/onboarding redirect chain has stripped `window.location.search`.
 * - Pre-auth visits run in feature-flags-only mode (no events) until the user
 *   grants cookie consent, so even an early track call can be dropped.
 *
 * Strategy:
 * 1. At app boot, parse `utm_source=email` query params synchronously and
 *    stash them in `sessionStorage`. This survives the redirect chain and
 *    page reloads within the same tab.
 * 2. After analytics becomes active (or the user identifies on signup/login),
 *    flush the stashed UTMs as `Email CTA Clicked` and clear the stash.
 */

import { safeAnalytics } from './analytics';
import { ANALYTICS_EVENTS } from '../constants/analytics';

const STORAGE_KEY = 'bower:pending_email_cta';

interface EmailCtaPayload {
  readonly utm_source: string;
  readonly utm_medium?: string;
  readonly utm_campaign?: string;
  readonly utm_content?: string;
  readonly utm_term?: string;
  readonly landing_path: string;
  readonly captured_at: string;
}

function readSearchParam(params: URLSearchParams, key: string): string | undefined {
  const value = params.get(key);
  return value === null ? undefined : value;
}

/**
 * Parse UTM params from the current URL. If utm_source=email, stash them in
 * sessionStorage for later flush. Safe to call multiple times — once a payload
 * is stashed it isn't overwritten by a later call without utm_source=email.
 *
 * Call this synchronously at app boot, before any redirect runs.
 */
export function stashEmailCtaFromUrl(): void {
  if (typeof window === 'undefined') return;

  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('utm_source') !== 'email') return;

    const payload: EmailCtaPayload = {
      utm_source: 'email',
      utm_medium: readSearchParam(params, 'utm_medium'),
      utm_campaign: readSearchParam(params, 'utm_campaign'),
      utm_content: readSearchParam(params, 'utm_content'),
      utm_term: readSearchParam(params, 'utm_term'),
      landing_path: window.location.pathname,
      captured_at: new Date().toISOString(),
    };

    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage can throw in private/locked-down contexts — non-fatal.
  }
}

/**
 * Fire `Email CTA Clicked` if a payload is stashed and analytics is able to
 * accept events. Idempotent: clears the stash on successful track so it won't
 * re-fire on subsequent calls within the same session.
 *
 * Call after analytics init, after consent grant, and after setUserId on
 * sign-in / sign-up so the event is attributed to the known person.
 */
export function flushPendingEmailCta(): void {
  if (typeof window === 'undefined') return;

  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return;
  }
  if (!raw) return;

  let payload: EmailCtaPayload;
  try {
    payload = JSON.parse(raw) as EmailCtaPayload;
  } catch {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return;
  }

  // safeAnalytics.track() is a no-op when not active (pre-init or
  // feature-flags-only). Only clear the stash once we actually fired.
  const before = window.sessionStorage.getItem(STORAGE_KEY);
  safeAnalytics.track(ANALYTICS_EVENTS.EMAIL_CTA_CLICKED, {
    utm_source: payload.utm_source,
    utm_medium: payload.utm_medium,
    utm_campaign: payload.utm_campaign,
    utm_content: payload.utm_content,
    utm_term: payload.utm_term,
    landing_path: payload.landing_path,
    captured_at: payload.captured_at,
  });

  // Heuristic: if the event was dropped (analytics inactive), the stash should
  // remain so a later call can retry. Since safeAnalytics.track is fire-and-
  // forget we can't see success — instead, gate on `hasAnalyticsConsent` /
  // active state via a public readiness check below.
  if (isAnalyticsActiveForCapture()) {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } else {
    // Leave the stash in place for a future flush. `before` is unused but
    // documents the read-modify-write intent.
    void before;
  }
}

/**
 * Mirror of safeAnalytics' internal `isActive` gate. Lives here because
 * `track()` swallows the no-op silently and we need to know whether to clear
 * the stash. Imports kept lazy to avoid a circular dep with analytics.ts.
 */
function isAnalyticsActiveForCapture(): boolean {
  try {
    // posthog-js exposes `__loaded` once init has run.
    const posthog = (
      window as unknown as {
        posthog?: { __loaded?: boolean; has_opted_in_capturing?: () => boolean };
      }
    ).posthog;
    if (!posthog?.__loaded) return false;
    if (typeof posthog.has_opted_in_capturing === 'function') {
      // Returns true when persistence !== memory, i.e. consent granted.
      return posthog.has_opted_in_capturing();
    }
    return true;
  } catch {
    return false;
  }
}
